(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.DSDOriginalsIntegrationR9 = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var ACCOUNT_INTERVAL_MS = 15000;
  var REQUEST_TIMEOUT_MS = 12000;
  var RETRY_DELAYS_MS = Object.freeze([1000, 3000, 10000, 30000]);

  function responseError(response, payload) {
    var error = new Error(payload && payload.error || 'request_failed');
    error.code = payload && payload.error || 'request_failed';
    error.status = response.status;
    error.payload = payload || {};
    return error;
  }

  function requestJson(url, options) {
    var nativeTimeoutSignal = typeof AbortSignal === 'function' && typeof AbortSignal.timeout === 'function'
      ? AbortSignal.timeout(REQUEST_TIMEOUT_MS)
      : null;
    var controller = !nativeTimeoutSignal && typeof AbortController === 'function' ? new AbortController() : null;
    var requestOptions = Object.assign({
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }, options || {});
    if (nativeTimeoutSignal) requestOptions.signal = nativeTimeoutSignal;
    else if (controller) requestOptions.signal = controller.signal;
    var timeoutId = null;
    var request = fetch(url, requestOptions).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (payload) {
        if (!response.ok) throw responseError(response, payload);
        return payload;
      });
    });
    if (nativeTimeoutSignal) {
      return request.catch(function (error) {
        if (error && (error.name === 'TimeoutError' || error.name === 'AbortError')) {
          var timeoutError = new Error('request_timeout');
          timeoutError.code = 'request_timeout';
          timeoutError.status = 408;
          throw timeoutError;
        }
        throw error;
      });
    }
    var timeout = new Promise(function (_, reject) {
      timeoutId = setTimeout(function () {
        var timeoutError = new Error('request_timeout');
        timeoutError.code = 'request_timeout';
        timeoutError.status = 408;
        if (controller) controller.abort();
        reject(timeoutError);
      }, REQUEST_TIMEOUT_MS);
    });
    return Promise.race([request, timeout]).finally(function () {
      if (timeoutId) clearTimeout(timeoutId);
    });
  }

  function create(config) {
    var endpointBase = String(config.endpointBase || '').replace(/\/+$/, '');
    var pendingRecord = null;
    var pendingTimer = null;
    var lastWriteAt = 0;
    var serverRevision = 0;
    var progressUrl = '';
    var progressScope = '';
    var progressGeneration = 0;
    var sessionRequestGeneration = 0;
    var currentSession = null;
    var retryRecord = null;
    var retryTimer = null;
    var retryAttempt = 0;
    var retrySequence = 0;
    var destroyed = false;

    function sequenceOf(record) {
      return Number(record && record.sequence || 0);
    }

    function retryable(error) {
      var status = Number(error && error.status || 0);
      return !status || status === 408 || status === 429 || status >= 500;
    }

    function clearRetryTimer() {
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = null;
    }

    function discardRetryThrough(record) {
      if (retryRecord && sequenceOf(retryRecord) <= sequenceOf(record)) retryRecord = null;
      if (!retryRecord) {
        clearRetryTimer();
        retrySequence = 0;
      }
    }

    function switchProgressContext(activeSession) {
      var nextUrl = String(activeSession && activeSession.progressUrl || '');
      var nextScope = String(activeSession && activeSession.progressScope || '');
      if (nextUrl === progressUrl && nextScope === progressScope) return;
      progressGeneration += 1;
      progressUrl = nextUrl;
      progressScope = nextScope;
      serverRevision = 0;
      lastWriteAt = 0;
      pendingRecord = null;
      if (pendingTimer) clearTimeout(pendingTimer);
      pendingTimer = null;
      retryRecord = null;
      clearRetryTimer();
      retryAttempt = 0;
      retrySequence = 0;
    }

    function sessionUrl(chapterId, audioRevision, format) {
      var query = new URLSearchParams({ chapterId: chapterId });
      if (audioRevision) query.set('audioRevision', audioRevision);
      if (format) query.set('format', format);
      return endpointBase + '/session?' + query.toString();
    }

    function session(chapterId, audioRevision) {
      if (destroyed) return Promise.reject(new Error('integration_destroyed'));
      var pageSignedIn = config.signedIn === true;
      if (pageSignedIn && !/^[a-f0-9]{64}$/.test(String(config.requestScope || ''))) {
        var identityError = new Error('account_bound_session_required');
        identityError.status = 409;
        return Promise.reject(identityError);
      }
      var pageScope = pageSignedIn ? config.requestScope : 'guest-page-v1';
      var requestGeneration = ++sessionRequestGeneration;
      function requestSession(format) {
        return requestJson(sessionUrl(chapterId, audioRevision, format), {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ readerScope: pageScope })
        });
      }
      return requestSession().catch(function (error) {
        if (!error || (error.status !== 401 && error.status !== 403)) throw error;
        return requestSession('read');
      }).then(function (result) {
        if (destroyed || requestGeneration !== sessionRequestGeneration || (config.signedIn === true) !== pageSignedIn || pageSignedIn && config.requestScope !== pageScope) {
          var stale = new Error('stale_session');
          stale.code = 'stale_session';
          throw stale;
        }
        switchProgressContext(result);
        currentSession = result;
        return result;
      });
    }

    function content(activeSession) {
      if (activeSession !== currentSession) return Promise.reject(new Error('stale_session'));
      return requestJson(activeSession.contentUrl, { method: 'GET' });
    }

    function resume(chapterId, audioRevision) {
      if (destroyed) return Promise.reject(new Error('integration_destroyed'));
      if (config.signedIn !== true || !/^[a-f0-9]{64}$/.test(String(config.requestScope || '')) ||
          !/^[a-z0-9][a-z0-9-]{0,79}$/.test(String(chapterId || '')) ||
          !/^[a-z0-9][a-z0-9-]{0,127}$/.test(String(audioRevision || ''))) return Promise.reject(new Error('resume_request_invalid'));
      // This read does not create a media grant or touch the progress writer.
      var generation = sessionRequestGeneration;
      var scope = config.requestScope;
      var query = new URLSearchParams({ chapterId: chapterId, audioRevision: audioRevision });
      return requestJson(endpointBase + '/resume?' + query.toString(), {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ readerScope: scope })
      }).then(function (record) {
        if (destroyed || generation !== sessionRequestGeneration || scope !== config.requestScope || config.signedIn !== true) throw new Error('stale_resume');
        return record;
      });
    }

    function seriesResume(chapters) {
      if (!Array.isArray(chapters) || !chapters.length || chapters.length > 8) return Promise.reject(new Error('series_resume_invalid'));
      if (destroyed || config.signedIn !== true || !/^[a-f0-9]{64}$/.test(String(config.requestScope || ''))) return Promise.reject(new Error('series_resume_scope_invalid'));
      var scope = config.requestScope, generation = sessionRequestGeneration;
      var ids = chapters.map(function (chapter) { return chapter.id; });
      return requestJson(endpointBase + '/progress-summary', {
        method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ chapterIds: ids, readerScope: scope })
      }).then(function (body) {
        if (destroyed || generation !== sessionRequestGeneration || scope !== config.requestScope) throw new Error('stale_resume');
        if (!body || body.version !== 1 || !Array.isArray(body.rows) || body.rows.length !== ids.length) throw new Error('series_resume_contract');
        var seen = new Set();
        body.rows.forEach(function (row) {
          if (!row || !ids.includes(row.chapterId) || seen.has(row.chapterId) || !['not_started', 'in_progress', 'finished'].includes(row.progress) ||
              row.lastPlayedAt !== null && (typeof row.lastPlayedAt !== 'string' || !Number.isFinite(Date.parse(row.lastPlayedAt)))) throw new Error('series_resume_contract');
          seen.add(row.chapterId);
        });
        var started = body.rows.filter(function (row) { return row.progress !== 'not_started' || row.lastPlayedAt !== null; });
        started.sort(function (a, b) { return (Date.parse(b.lastPlayedAt) || 0) - (Date.parse(a.lastPlayedAt) || 0); });
        var chapter = chapters.find(function (chapter) { return chapter.id === (started[0] && started[0].chapterId); }) || chapters[0];
        return resume(chapter.id, chapter.audioRevision);
      });
    }

    function progress(activeSession) {
      if (activeSession !== currentSession) return Promise.reject(new Error('stale_session'));
      switchProgressContext(activeSession);
      if (activeSession.mode === 'read' || !progressUrl) return Promise.resolve({ skipped: true });
      var generation = progressGeneration;
      return requestJson(progressUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ progressScope: progressScope })
      }).then(function (record) {
        if (generation === progressGeneration && activeSession === currentSession) serverRevision = Number(record.serverRevision || 0);
        return record;
      });
    }

    function write(record, keepalive) {
      if (destroyed || !progressUrl) return Promise.resolve({ skipped: true });
      var targetUrl = progressUrl;
      var generation = progressGeneration;
      var payload = Object.assign({}, record, { baseServerRevision: serverRevision, progressScope: progressScope });
      return requestJson(targetUrl, {
        method: 'PUT',
        keepalive: Boolean(keepalive),
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (saved) {
        if (generation === progressGeneration) {
          lastWriteAt = Date.now();
          serverRevision = Number(saved.serverRevision || serverRevision);
        }
        return saved;
      }).catch(function (error) {
        if (generation === progressGeneration && error.status === 409 && error.payload && error.payload.current) {
          serverRevision = Number(error.payload.current.serverRevision || serverRevision);
        }
        throw error;
      });
    }

    function scheduleRetry(record) {
      if (destroyed || !progressUrl) return;
      if (sequenceOf(record) > retrySequence) retryAttempt = 0;
      retrySequence = Math.max(retrySequence, sequenceOf(record));
      if (!retryRecord || sequenceOf(record) >= sequenceOf(retryRecord)) retryRecord = record;
      if (retryTimer || retryAttempt >= RETRY_DELAYS_MS.length) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      var delay = RETRY_DELAYS_MS[retryAttempt];
      retryAttempt += 1;
      retryTimer = setTimeout(function () {
        var next = retryRecord;
        retryRecord = null;
        retryTimer = null;
        if (!next || destroyed) return;
        writeWithRetry(next, false).catch(function () {});
      }, delay);
    }

    function writeWithRetry(record, keepalive) {
      var generation = progressGeneration;
      return write(record, keepalive).then(function (saved) {
        if (generation === progressGeneration) {
          discardRetryThrough(record);
          retryAttempt = 0;
        }
        return saved;
      }).catch(function (error) {
        if (generation === progressGeneration) {
          if (retryable(error)) scheduleRetry(record);
          else discardRetryThrough(record);
        }
        throw error;
      });
    }

    function retryWhenOnline() {
      if (!retryRecord || destroyed) return;
      retryAttempt = 0;
      scheduleRetry(retryRecord);
    }

    if (typeof window !== 'undefined' && window.addEventListener) window.addEventListener('online', retryWhenOnline);

    function schedule(record) {
      pendingRecord = record;
      if (pendingTimer) return Promise.resolve({ queued: true });
      var wait = Math.max(0, ACCOUNT_INTERVAL_MS - (Date.now() - lastWriteAt));
      pendingTimer = setTimeout(function () {
        var next = pendingRecord;
        pendingRecord = null;
        pendingTimer = null;
        writeWithRetry(next, false).catch(function () {});
      }, wait);
      return Promise.resolve({ queued: true });
    }

    function save(record, immediate) {
      if (destroyed || !progressUrl) return Promise.resolve({ skipped: true });
      if (immediate || Date.now() - lastWriteAt >= ACCOUNT_INTERVAL_MS) {
        pendingRecord = null;
        if (pendingTimer) clearTimeout(pendingTimer);
        pendingTimer = null;
        return writeWithRetry(record, false);
      }
      return schedule(record);
    }

    function flush(record) {
      pendingRecord = null;
      if (pendingTimer) clearTimeout(pendingTimer);
      pendingTimer = null;
      return writeWithRetry(record, true);
    }

    function destroy() {
      sessionRequestGeneration += 1;
      currentSession = null;
      if (pendingTimer) clearTimeout(pendingTimer);
      pendingTimer = null;
      pendingRecord = null;
      clearRetryTimer();
      retryRecord = null;
      retrySequence = 0;
      progressGeneration += 1;
      progressUrl = '';
      progressScope = '';
      serverRevision = 0;
      destroyed = true;
      if (typeof window !== 'undefined' && window.removeEventListener) window.removeEventListener('online', retryWhenOnline);
    }

    return { session: session, content: content, resume: resume, seriesResume: seriesResume, progress: progress, save: save, flush: flush,
      reconcile: function (record) { return write(record, false); }, destroy: destroy };
  }

  return { ACCOUNT_INTERVAL_MS: ACCOUNT_INTERVAL_MS, REQUEST_TIMEOUT_MS: REQUEST_TIMEOUT_MS, RETRY_DELAYS_MS: RETRY_DELAYS_MS, create: create };
});
